import * as React from 'react'
import styled from 'styled-components'
import httpClient from '../../core/httpClient'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'

declare var window: interfaces.IWindow

export type TemplateResource =
  | 'tour_memo_templates'
  | 'sound_out_templates'
  | 'formal_request_templates'

interface IProps {
  resource: TemplateResource
  // デフォルトの紐づけ先post(set_default API に渡す)
  postId: number
  // is_default を持たないテンプレは default_post_ids に postId が含まれるかでデフォルトを判定する
  templates: any[]
  // 指定時は「通常ガイド」「〈ランク名〉(特殊ガイド)」のグループごとに表示する(テンプレが1件以上あるグループのみ見出しを出す)
  // 省略時はグループ分けせず1つのリストにする(ツアーメモ)
  specialRanks?: interfaces.IAgentRank[]
  // テンプレが1件も無いときの表示
  emptyNote?: React.ReactNode
}

// テンプレ(打診・正式依頼・ツアーメモ)の一覧とデフォルト切替(set_default API)。ツアー設定タブとDMCツアー編集で共用
export const DefaultTemplateGroups: React.FC<IProps> = props => {
  const { resource, postId, specialRanks, emptyNote } = props
  const [templates, setTemplates] = React.useState<any[]>(() =>
    (props.templates || []).map(t => ({
      ...t,
      is_default: t.is_default ?? (t.default_post_ids || []).includes(postId),
    }))
  )

  const handleSetDefault = async (template: any) => {
    try {
      const { data } = await httpClient.put(`/agent/api/${resource}/${template.id}/set_default`, {
        post_id: postId,
      })
      // 同じグループ(agent_rank_id)内でのみデフォルトを付け替える(ツアーメモは全件同一グループ)
      setTemplates(prev =>
        prev.map(t =>
          (t.agent_rank_id ?? null) === (template.agent_rank_id ?? null)
            ? { ...t, is_default: t.id === template.id }
            : t
        )
      )
      window.flashMessages.addMessage({ text: data.flush.message, type: data.flush.type })
    } catch (e) {
      const message =
        e.response?.data?.flush?.message || e.response?.data?.errors || 'エラーが発生しました'
      window.flashMessages.addMessage({ text: message, type: 'error' })
    }
  }

  const groups = specialRanks
    ? [
        { rankId: null, label: '通常ガイド' },
        ...specialRanks.map(rank => ({ rankId: rank.id, label: `${rank.name}（特殊ガイド）` })),
      ]
        .map(group => ({
          ...group,
          templates: templates.filter(t => (t.agent_rank_id ?? null) === group.rankId),
        }))
        .filter(group => group.templates.length > 0)
    : [{ rankId: null, label: null, templates }]

  return (
    <Groups>
      {groups.length === 0 && emptyNote}
      {groups.map(group => (
        <div key={group.rankId ?? 'normal'}>
          {group.label && <GroupTitle>{group.label}</GroupTitle>}
          <TemplateList>
            {group.templates.map(template => (
              <TemplateRow
                key={template.id}
                className={template.is_default ? 'is-default' : undefined}
              >
                <TemplateRadioLabel>
                  <TemplateRadio
                    type="radio"
                    name={`${resource}_default_${postId}_${group.rankId ?? 'normal'}`}
                    checked={!!template.is_default}
                    onChange={() => {
                      if (!template.is_default) {
                        handleSetDefault(template)
                      }
                    }}
                  />
                  <TemplateTitle>{template.title}</TemplateTitle>
                </TemplateRadioLabel>
                <TemplateActions>
                  {template.is_default && <DefaultBadge>デフォルト</DefaultBadge>}
                  <EditLink
                    href={`/agent/${resource}/${template.id}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    編集
                  </EditLink>
                </TemplateActions>
              </TemplateRow>
            ))}
          </TemplateList>
        </div>
      ))}
    </Groups>
  )
}

const Groups = styled.div`
  > div + div {
    margin-top: 16px;
  }
`

const GroupTitle = styled.p`
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 6px;
`

const TemplateList = styled.div`
  border-top: 1px solid ${constants.COLORS.divider};
`

const DefaultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  font-size: 12px;
  padding: 0 10px;
  border-radius: 20px;
  border: 1px solid ${constants.COLORS.successBorder2};
  background: ${constants.COLORS.successBG2};
  color: ${constants.COLORS.primary};
  font-weight: 700;
`

const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 6px 10px;
  border-bottom: 1px solid ${constants.COLORS.divider};

  &.is-default {
    background: ${constants.COLORS.successBG2};
    box-shadow: inset 4px 0 0 ${constants.COLORS.primary};
  }
`

const TemplateRadio = styled.input`
  appearance: none;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 0;
  border: 2px solid ${constants.COLORS.divider};
  border-radius: 50%;
  background: ${constants.COLORS.white};
  cursor: pointer;

  &:checked {
    border-color: ${constants.COLORS.primary};
    background: ${constants.COLORS.primary};
    box-shadow: inset 0 0 0 4px ${constants.COLORS.white};
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${constants.COLORS.disabled};
    background: ${constants.COLORS.lightGray};
  }
`

const TemplateRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  max-width: 68%;
  min-width: 0;
  cursor: pointer;
`

const TemplateTitle = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TemplateActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`

const EditLink = styled.a`
  font-size: 12px;
  font-weight: 700;
  color: ${constants.COLORS.primary};
  text-decoration: underline;
  white-space: nowrap;
`

export default DefaultTemplateGroups
